import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Box, Typography, Button, Stack, IconButton } from '@mui/material';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import Dialog from '../../../Components/Dialog';

const isCsvFile = (file) =>
  !!file &&
  (file.type === 'text/csv' ||
    file.type === 'application/vnd.ms-excel' ||
    file.name?.toLowerCase().endsWith('.csv'));

// The uploaded file name must contain the selected branch name (case-insensitive).
// With no branch name there is nothing to enforce, so it is treated as valid.
const fileMatchesBranch = (file, branchName) => {
  if (!branchName?.trim()) return true;
  return (
    !!file && file.name.toLowerCase().includes(branchName.trim().toLowerCase())
  );
};

/**
 * Body of the import dialog: a drag-and-drop area with a browse fallback, the
 * branch-name requirement, a preview of the selected file and a validation error
 * when its name does not contain the branch name. The `onClose` prop is injected
 * by the shared Dialog wrapper (it clones its children) and is unused here.
 */
const DropZone = function DropZone({
  file = null,
  branchName = '',
  hasError = false,
  onSelectFile = () => {},
  onClearFile = () => {},
  // eslint-disable-next-line no-unused-vars
  onClose = () => {},
}) {
  const intl = useIntl();
  const t = (id) => intl.messages[id] ?? id;
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const pickFromList = (fileList) => {
    const selected = fileList?.[0];
    if (isCsvFile(selected)) onSelectFile(selected);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    pickFromList(event.dataTransfer.files);
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      {branchName?.trim() && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            padding: '8px 12px',
            borderRadius: '8px',
            backgroundColor: 'action.hover',
            color: 'text.secondary',
          }}
        >
          <InfoOutlinedIcon fontSize="small" color="info" />
          <Typography variant="body2">
            {intl.formatMessage(
              { id: 'FILE_NAME_MUST_CONTAIN_BRANCH' },
              { branch: branchName }
            )}
          </Typography>
        </Stack>
      )}
      <Box
        onDrop={handleDrop}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          setIsDragging(false);
        }}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          padding: '40px 24px',
          cursor: 'pointer',
          border: '2px dashed',
          borderColor: isDragging ? 'primary.main' : 'divider',
          borderRadius: '12px',
          backgroundColor: isDragging ? 'action.hover' : 'background.default',
          transition: 'border-color 0.2s ease, background-color 0.2s ease',
        }}
      >
        <UploadFileRoundedIcon sx={{ fontSize: 48, color: 'primary.main' }} />
        <Typography variant="body1" textAlign="center">
          {t('DRAG_AND_DROP_FILE_HERE')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('OR')}
        </Typography>
        <Button
          variant="outlined"
          onClick={(event) => {
            event.stopPropagation();
            fileInputRef.current?.click();
          }}
        >
          {t('BROWSE')}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          hidden
          onChange={(event) => {
            pickFromList(event.target.files);
            event.target.value = ''; // allow re-selecting the same file
          }}
        />
      </Box>
      {file && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            padding: '8px 12px',
            borderRadius: '8px',
            backgroundColor: hasError ? 'error.light' : 'action.hover',
            border: hasError ? '1px solid' : 'none',
            borderColor: 'error.main',
          }}
        >
          <InsertDriveFileRoundedIcon color={hasError ? 'error' : 'primary'} />
          <Typography variant="body2" noWrap sx={{ flexGrow: 1 }}>
            {file.name}
          </Typography>
          <IconButton size="small" onClick={onClearFile}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>
      )}
      {hasError && (
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <ErrorOutlineRoundedIcon fontSize="small" color="error" />
          <Typography variant="body2" color="error.main">
            {intl.formatMessage(
              { id: 'FILE_NAME_BRANCH_ERROR' },
              { branch: branchName }
            )}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

DropZone.propTypes = {
  file: PropTypes.object,
  branchName: PropTypes.string,
  hasError: PropTypes.bool,
  onSelectFile: PropTypes.func,
  onClearFile: PropTypes.func,
  onClose: PropTypes.func,
};

const ImportPracticesDialog = function ImportPracticesDialog({
  open = false,
  branchName = '',
  onClose = () => {},
  onConfirm = () => {},
}) {
  const [file, setFile] = useState(null);

  const nameValid = fileMatchesBranch(file, branchName);

  const handleClose = () => {
    setFile(null);
    onClose();
  };

  const handleConfirm = () => {
    if (!file || !nameValid) return;
    onConfirm(file);
    setFile(null);
  };

  return (
    <Dialog
      id="import-practices-dialog"
      open={open}
      onClose={handleClose}
      onSubmit={handleConfirm}
      submitLabel="IMPORT"
      disableSubmit={!file || !nameValid}
      title="IMPORT_PRACTICES_CSV"
      width="sm"
      fullWidth
      icon={<UploadFileRoundedIcon />}
    >
      <DropZone
        file={file}
        branchName={branchName}
        hasError={!!file && !nameValid}
        onSelectFile={setFile}
        onClearFile={() => setFile(null)}
      />
    </Dialog>
  );
};

ImportPracticesDialog.propTypes = {
  open: PropTypes.bool,
  branchName: PropTypes.string,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
};

export default ImportPracticesDialog;
